import Flex from "@/components/Flex/Flex";
import Button from "@/components/forms/Button/Button";
import { Table, TableColumn } from "@/components/Table/Table";
import { BuiltQuery, BuiltQueryKeepa, ParseError } from "@boxem/core-query-language";
import { useEffect, useState } from "react";
import { QueryAwareTextInput } from "@/components/QueryAwareTextInput/QueryAwareTextInput";
import IconSearch from "@/components/icons/IconSearch";
import { BoxemProductFinderBrand, BoxemProductFinderResponseProduct } from "@boxem/core-api-schema";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import CopyToClipBoard from "@/components/CopyToClipboard/CopyToClipboard";
import ProductImage from "@/components/ProductImage/ProductImage";
import Alert from "@/components/Alert/Alert";
import { formatValueAsCurrency } from "@/utils/formatUtils";
import Accordion from "@/components/Accordion/Accordion";
import IconInfo from "@/components/icons/IconInfo";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import { productFinder } from "@/api/product-research";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";

import styles from "./ProductResearchSearchResultsTable.module.css";


const searchTimeout = { id: 0 };
export function ProductResearchSearchResultsTable() {
  const { handleErrorResponseGenerically } = useErrorModalContext();

  const [query, setQuery] = useState<string>("");
  const [queryResult, setQueryResult] = useState<{ builtQuery: BuiltQuery[], builtQueryKeepa?: BuiltQueryKeepa, parseError?: ParseError }>(null);

  // @TODO pagination
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);

  const [searchResults, setSearchResults] = useState<BoxemProductFinderResponseProduct[]>([]);
  const [searchBrands, setSearchBrands] = useState<BoxemProductFinderBrand[]>([]);
  const [searchView, setSearchView] = useState<BoxemProductFinderResponseProduct[]>([]);
  const [searchResultsLoading, setSearchResultsLoading] = useState<boolean>(false);

  const debouncedSearch = async () => {
    clearTimeout(searchTimeout.id);
    searchTimeout.id = window.setTimeout(async () => {
      setSearchResultsLoading(true);
      productFinder(query)
        .then(result => {
          setSearchResults(result.products);
          setSearchView(result.products);
          setSearchBrands(result.brands);
        })
        .catch(e => {
          handleErrorResponseGenerically(e);
        })
        .finally(() => {
          setSearchResultsLoading(false);
        })
      setSearchResults([]);
    }, 350);
  };

  const columns: TableColumn<BoxemProductFinderResponseProduct>[] = [
    {
      title: 'Product Description',
      dataKey: 'name',
      id: 'products',
      alignment: "left",
      renderRowCell: (row) => (
        <Flex gap={2} align="center" className={styles.productCell}>
          <ProductImage
            imageUrl={row.getCellData('image0')}
          />
          <Flex direction="column">
            <Tooltip title={row.getCellData('name')}>
              <a className={styles.minWidth0} href={`https://www.amazon.com/dp/${row.getCellData('asin')}`} target="_blank">{row.getCellData('name')}</a>
            </Tooltip>
            <CopyToClipBoard value={row.getCellData('asin')} tooltipText={"Copy ASIN"}>
              <span className={`weak`}>ASIN: <span className="strong">{row.getCellData('asin')}</span></span>
            </CopyToClipBoard>
          </Flex>
        </Flex>
      ),
    },
    {
      title: "Sales Ranks & Categories",
      dataKey: "salesRanks",
      id: "category&salesRanks",
      alignment: "left",
      renderRowCell(row, column, table) {
        return (
          <Flex direction="column" align="start" gap={1} className={styles.productCell}>
            {row.getCellData("salesRanks").map((salesRank, i) => (
              <Flex gap={2} key={i}>
                <span className={`small-text ${styles.nowrap}`}><span className={`strong`}>#{salesRank.rank}</span>, Top <span className={`strong`}>{+Math.abs(((salesRank.rank - salesRank.lowestRank) / (salesRank.highestRank - salesRank.lowestRank)) * 100).toFixed(2)}%</span> in </span>
                <Tooltip
                  title={salesRank.categoryName}
                  placement="auto"
                  children={
                    <span className={`small-text`}>
                      <a className={`${styles.categoryName}`} href={`https://www.amazon.com/b/?node=${salesRank.categoryId}`} target="_blank">
                        {salesRank.categoryName}
                      </a>
                    </span>
                  }
                />
                {/* <span className="small-text">out of {salesRank.productCount} products</span> */}
              </Flex>
            ))}
          </Flex>
        )
      },
    },
    {
      title: "Pricing & Offers",
      id: "pricing&Offers",
      alignment: "left",
      renderRowCell(row, column, table) {
        return (
          <Flex direction="column" gap={1} className={styles.productCell}>
            <span>{row.getCellData("offerCount")} Live Offer(s) (all conditions)</span>
            <Flex align="center" gap={2}>
              <span>Buy Box: </span>
              <span className={"strong"}>{formatValueAsCurrency(row.getCellData('buyBoxPrice') / 100)}</span>
            </Flex>
          </Flex>
        )
      },
    },
    {
      title: "Brand & Deals",
      dataKey: "brand",
      id: "brand",
      alignment: "left",
      renderRowCell: (row) => {
        const rowData = row.rowData;
        return (
          <Flex direction="column" gap={2} className={styles.productCell}>
            <Flex align="center" gap={2}>
              <span className="weak">Brand: </span>
              <span className="strong">{row.getCellData('brand')}</span>
            </Flex>
          </Flex>
        );
      }
    },
  ];

  return (
    <Flex direction="column" gap={4}>
      <Flex direction="column" gap={4} grow={1} shrink={0}>
        <QueryAwareTextInput
          loading={searchResultsLoading}
          query={query}
          setQuery={setQuery}
          onChange={(builtQuery: BuiltQuery[], builtQueryKeepa: BuiltQueryKeepa, parseError: ParseError) => {
            setQueryResult({ builtQuery, builtQueryKeepa, parseError });
          }}
          onSearch={() => { debouncedSearch(); }}
          clearSearch={() => {
            setSearchBrands([]);
            setSearchResults([]);
            setSearchView([]);
          }}
        />
      </Flex>

      {searchBrands.length > 0 && (
        <Accordion
          title={
            <Flex align="center" gap={2}>
              <span className="strong">Brands ({searchBrands.length})</span>
              <Tooltip
                title={"These brands appear in search results. Most-common brands will be shown first on the left"}
                children={<IconInfo />}
                placement="right"
              />
            </Flex>
          }
          defaultExpanded={true}
          children={
            <Flex gap={4} className={styles.brandCards}>
              {searchBrands.sort((a, b) => b.productCount - a.productCount).map((brand, i) => (
                <Flex direction="column" align="center" className={styles.brandCard} gap={2} key={i}>
                  <h3>{brand.name}</h3>
                  <p className="small-text">{brand.productCount} products</p>

                  <Flex direction="column" gap={2}>
                    {brand.capitalOneUrl && (
                      <Flex align="center" gap={2}>
                        <a href={brand.capitalOneUrl} target="_blank">Capital One Shopping</a>
                      </Flex>
                    )}

                    {brand.cardbearUrl && (
                      <Flex align="center" gap={2}>
                        <a href={brand.cardbearUrl} target="_blank">Cardbear</a>
                      </Flex>
                    )}

                    {brand.giftCardWikiUrl && (
                      <Flex align="center" gap={2}>
                        <a href={brand.giftCardWikiUrl} target="_blank">Gift Card Wiki</a>
                      </Flex>
                    )}

                    {brand.topCashbackUrl && (
                      <Flex align="center" gap={2}>
                        <a href={brand.topCashbackUrl} target="_blank">TopCashback</a>
                      </Flex>
                    )}

                    {brand.couponBirdsUrl && (
                      <Flex align="center" gap={2}>
                        <a href={brand.couponBirdsUrl} target="_blank">Coupon Birds</a>
                      </Flex>
                    )}

                    {brand.cashbackMonitorUrl && (
                      <Flex align="center" gap={2}>
                        <a href={brand.cashbackMonitorUrl} target="_blank">Cashback Monitor</a>
                      </Flex>
                    )}

                    {brand.slickDealsUrl && (
                      <Flex align="center" gap={2}>
                        <a href={brand.slickDealsUrl} target="_blank">Slick Deals</a>
                      </Flex>
                    )}

                    {brand.couponFollowUrl && (
                      <Flex align="center" gap={2}>
                        <a href={brand.couponFollowUrl} target="_blank">Coupon Follow</a>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          }
        />
      )}

      {(searchView?.length > 0 || searchResultsLoading) && (
        <Table
          isLoading={searchResultsLoading}
          className={styles.table}
          columnControls={false}
          columns={columns}
          data={searchView}
          noResultsState={(!searchResultsLoading && searchResults.length === 0 && (!!queryResult?.builtQuery && !queryResult?.parseError)) &&
            <Flex direction="column" justify="center" align="center" gap={8} className={styles.zeroState}>
              <img src="/images/search.svg" height="100" />
              <span className="strong">
                {`No results for this query: "${query}"`}
              </span>
            </Flex>
          }
          zeroState={(!searchResultsLoading && searchResults.length === 0 && (!query || query.trim().length === 0)) &&
            <Flex direction="column" justify="center" align="center" gap={8} className={styles.zeroState}>
              <img src="/images/search.svg" height="100" />
              <span>
                {`Make a search by typing in the advanced search box above, or using the manual query builder!`}
              </span>
            </Flex>
          }
          loadingState={
            <Flex direction="column" justify="center" align="center" gap={8} className={styles.zeroState}>
              <Flex direction="column" gap={2} align="center">
                <p>Loading...</p>
                <LoadingSpinner/>
              </Flex>
              <span>Don't go anywhere! This can take a few minutes.</span>
            </Flex>
          }
        />
      )}
    </Flex>
  );
}
